import { useState } from "react";
import { MageXPatient } from "@prisma/client";
import { ResponsiveLine } from "@nivo/line";
import { Trans } from "@lingui/macro";
import dayjs from "dayjs";

interface ScoreDisplayProps {
  property: keyof MageXPatient;
  aggregatedValue: Array<Score>;
  isAlwaysHovered?: boolean;
  onlyTruth?: boolean;
  hasValidatedTruth?: boolean;
  correctedValue?: Array<Score> | null;
  /** Le geste gouvernant est une CORRECTION. C'est la seule chose qui met la courbe en
   *  vert : une série que le modèle a produite reste bleue même validée, parce que les
   *  chiffres sont les siens. */
  isCorrected?: boolean;
}

type Score = {
  score: number;
  date: string;
};

export default function ValueDisplayScore({
  property,
  aggregatedValue,
  isAlwaysHovered = false,
  onlyTruth = false,
  hasValidatedTruth = false,
  correctedValue,
  isCorrected = false,
}: ScoreDisplayProps) {
  try {
    const [isHovered, setIsHovered] = useState(false);

    // La vérité par champ, telle que ``ValueAndPropertyDisplay`` l'a résolue depuis le
    // geste gouvernant.
    //
    // Ce composant lisait ``patientTruth?.[property]`` — la colonne brute — ce qui en
    // faisait une quatrième implémentation de la précédence d'affichage, et la seule à
    // ignorer le journal : une correction dont la colonne n'a pas encore été écrite, ou
    // une absence confirmée, n'y était pas visible.
    const truthScores = Array.isArray(correctedValue) ? correctedValue : [];
    const aggregatedScores = Array.isArray(aggregatedValue) ? aggregatedValue : [];

    // Dès qu'un clinicien a statué, c'est sa réponse que le dossier affirme — la même
    // règle que partout ailleurs sur la page. Ce composant y dérogeait : il empilait la
    // série du modèle SOUS celle de la vérité, si bien qu'un point supprimé restait
    // tracé. Dans la vignette, qui n'a ni légende, ni points, ni infobulle, il était
    // rigoureusement indiscernable d'un point conservé.
    const showsTruth = hasValidatedTruth || onlyTruth;
    const displayedScores = showsTruth ? truthScores : aggregatedScores;

    // Vide des deux côtés : jamais extrait, ou absence confirmée. Les deux se disent
    // « Non déterminé » et c'est voulu — la distinction est portée par la pastille, pas
    // par la valeur.
    if (displayedScores.length === 0) {
      return (
        <span className="font-medium text-right block text-slate-400 italic">
          <Trans>Non déterminé</Trans>
        </span>
      );
    }

    if (
      displayedScores.length === 1 &&
      Number.isFinite(displayedScores[0]?.score)
    ) {
      return (
        <span className="font-medium">
          {displayedScores[0]!.score}{" "}
          <small className="text-xs text-slate-500">
            ({dayjs(displayedScores[0]!.date).format("DD/MM/YYYY")})
          </small>
        </span>
      );
    }

    const buildSeries = (scores: Array<Score>, id: string) => {
      const valid = scores.filter(
        (s) => Number.isFinite(s.score) && dayjs(s.date).isValid(),
      );
      if (valid.length === 0) return null;
      return {
        id,
        data: valid
          .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
          .map((score) => ({
            x: dayjs(score.date).format("DD/MM/YYYY"),
            y: score.score,
          })),
      };
    };

    // Vert = corrigé par un clinicien, bleu = les chiffres du modèle. La couleur suit le
    // GESTE et non la simple existence d'une vérité : une série que le médecin a validée
    // sans y toucher reste celle du modèle, et l'annoncer en vert dirait qu'il l'a
    // réécrite. Une couleur unique, calculée une fois, plutôt qu'une fonction qui compare
    // un identifiant : nivo passe aux points un id de la forme ``série.index``, si bien
    // que l'égalité stricte échouait toujours et que les points restaient bleus sur une
    // courbe verte.
    const seriesColor = isCorrected ? "#22c55e" : "#3b82f6";
    const primarySeries = buildSeries(
      displayedScores,
      isCorrected ? `${property}-corrected` : `${property}-score`,
    );

    // UNE série, partout. Dès qu'un clinicien a corrigé la série, c'est sa réponse que le
    // dossier affirme — la même règle que sur tous les autres champs de la page.
    //
    // Le grand graphique superposait encore la série du modèle à celle de la vérité. Deux
    // courbes, aucune légende — l'information « laquelle est laquelle » n'existait que
    // dans l'infobulle d'un point survolé, en anglais codé en dur. Un point supprimé par
    // le médecin restait donc tracé, et rien à l'écran ne disait qu'il avait été supprimé.
    const chartData = primarySeries ? [primarySeries] : [];
    const sparklineData = chartData;

    function renderHoveredContent() {
      return (
        <div className="w-96 h-64 mb-2">
          <ResponsiveLine
            data={chartData}
            margin={{ top: 20, right: 30, bottom: 60, left: 50 }}
            xScale={{ type: "point" }}
            yScale={{
              type: "linear",
              min: "auto",
              max: "auto",
            }}
            curve="monotoneX"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              format: (_value) => _value,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            enableGridX={false}
            enableGridY={true}
            pointSize={6}
            pointColor="#ffffff"
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            colors={[seriesColor]}
            lineWidth={2}
            isInteractive={true}
            animate={true}
            tooltip={({ point }) => (
              <div className="bg-white p-2 border border-slate-300 rounded shadow">
                <div className="text-sm">
                  <strong>
                    <Trans>Date</Trans> :
                  </strong>{" "}
                  {point.data.x}
                </div>
                <div className="text-sm">
                  <strong>
                    <Trans>Score</Trans> :
                  </strong>{" "}
                  {point.data.y}
                </div>
                <div className="text-sm text-slate-500">
                  {isCorrected ? (
                    <Trans>Valeur corrigée</Trans>
                  ) : (
                    <Trans>Valeur agrégée</Trans>
                  )}
                </div>
              </div>
            )}
          />
        </div>
      );
    }

    if (isAlwaysHovered) {
      return <>{renderHoveredContent()}</>;
    }

    return (
      <div
        className="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="w-24 h-8">
          <ResponsiveLine
            data={sparklineData}
            margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
            xScale={{ type: "point" }}
            yScale={{
              type: "linear",
              min: "auto",
              max: "auto",
            }}
            curve="monotoneX"
            axisTop={null}
            axisRight={null}
            axisBottom={null}
            axisLeft={null}
            enableGridX={false}
            enableGridY={false}
            pointSize={0}
            pointBorderWidth={0}
            pointLabelYOffset={-12}
            enableArea={false}
            colors={[seriesColor]}
            lineWidth={2}
            isInteractive={false}
            animate={false}
            enableSlices={false}
          />
        </div>

        {isHovered && (
          <div className="absolute top-full left-0 z-50 mt-2 p-3 bg-white border border-slate-200 rounded-lg shadow-lg">
            {renderHoveredContent()}
          </div>
        )}
      </div>
    );
  } catch (error) {
    console.error("Error displaying score", error, {
      property,
      aggregatedValue,
      isAlwaysHovered,
      onlyTruth,
    });

    return null;
  }
}
